import { useOakPad } from "@/context/OakPadContext";
import { toast } from "sonner";
import { useMemo, useState } from "react";
import { jsPDF } from "jspdf";
import {
  NotebookPen, X, Trash2, FileDown, Share2, ChevronDown, ChevronUp, ClipboardCopy, Globe2, Loader2, Sparkles,
} from "lucide-react";
import { api } from "@/lib/api";

const PUBLISH_NUDGE_KEY = "oak_publish_nudge_dismissed_v1";
const PUBLISH_NUDGE_MIN = 3;

/**
 * OAK-Pad — slide-up drawer holding all snippets the user has clipped via
 * long-press. Branded PDF export (jspdf) + native Web Share API. Mounted
 * once at the app root by OakPadGate.
 */
export default function OakPad() {
  const { snippets, open, closePad, openPad, removeSnippet, clearAll, synced } = useOakPad();
  const [expanded, setExpanded] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const canPublish = snippets.length >= 3;

  // "Publish your study guide" discovery nudge — appears above the FAB once
  // the user has clipped 3+ snippets in a session, until they dismiss or
  // publish. Stored in sessionStorage so it doesn't re-pester within the
  // same browser session, but does come back next visit if they still
  // haven't published.
  const [nudgeDismissed, setNudgeDismissed] = useState(() => {
    try { return sessionStorage.getItem(PUBLISH_NUDGE_KEY) === "1"; } catch { return false; }
  });
  const showPublishNudge = !open && canPublish && !nudgeDismissed && !publishing;
  const dismissNudge = () => {
    try { sessionStorage.setItem(PUBLISH_NUDGE_KEY, "1"); } catch {}
    setNudgeDismissed(true);
  };

  const publishStudyGuide = async () => {
    if (!canPublish) {
      toast.error("Clip at least 3 snippets first.");
      return;
    }
    const titleRaw = window.prompt(
      "Title for your study guide (visible to anyone with the link):",
      snippets[0]?.source_title?.slice(0, 60) || "My OAK study guide"
    );
    if (!titleRaw) return;
    const title = titleRaw.trim().slice(0, 120);
    if (title.length < 3) {
      toast.error("Title is too short.");
      return;
    }
    setPublishing(true);
    try {
      const { data } = await api.post("/notes/publish", {
        title,
        snippets: snippets.map((s) => ({
          text: s.text,
          source_title: s.source_title || null,
          source_path_id: s.source_path_id || null,
        })),
      });
      const apiOrigin = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
      const shareUrl = `${apiOrigin}/api/og/notes/${data.slug}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success("Published! Share link copied to clipboard.", {
          description: shareUrl,
          duration: 8000,
        });
      } catch {
        toast.success(`Published at ${shareUrl}`);
      }
      // Native share fallback for mobile
      if (navigator.share) {
        try {
          await navigator.share({
            title,
            text: `My Oak AI Campus study guide on "${title}".`,
            url: shareUrl,
          });
        } catch (e) {
          // ignore — user dismissed
        }
      }
      // Dismiss the publish-nudge for the rest of the session — they've
      // shipped, no need to keep prodding.
      try { sessionStorage.setItem(PUBLISH_NUDGE_KEY, "1"); } catch {}
      setNudgeDismissed(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    } finally {
      setPublishing(false);
    }
  };

  const pageTitle = "Oak AI Campus · OAK-Pad Notes";
  const totalChars = useMemo(
    () => snippets.reduce((acc, s) => acc + s.text.length, 0),
    [snippets]
  );

  // Floating toggle that lets users pop the pad open from anywhere. Hides
  // when the pad is open to avoid double UI.
  if (!open) {
    return (
      <>
        {showPublishNudge && (
          <div
            className="fixed right-4 md:right-8 bottom-[9.5rem] md:bottom-[5.5rem] z-[60] pointer-events-auto inline-flex items-center gap-1 rounded-full pl-3 pr-1 py-1 bg-[#0A192F]/95 border border-[#00FF9D]/45 shadow-[0_12px_40px_-12px_rgba(0,255,157,0.45)] animate-fadeup"
            data-testid="oakpad-publish-nudge"
          >
            <button
              type="button"
              onClick={publishStudyGuide}
              className="inline-flex items-center gap-1.5 text-[12px] font-display font-semibold text-[#9bffcc] hover:text-[#00FF9D] px-1.5 py-1 transition"
              data-testid="oakpad-publish-nudge-cta"
              title="Publish your snippets as a public study guide"
            >
              <Sparkles size={13} className="text-[#00FF9D]" />
              Publish your study guide
            </button>
            <button
              type="button"
              onClick={dismissNudge}
              className="rounded-full p-1 text-white/40 hover:text-white hover:bg-white/10 transition"
              aria-label="Dismiss publish nudge"
              data-testid="oakpad-publish-nudge-dismiss"
            >
              <X size={12} />
            </button>
          </div>
        )}
        <button
          type="button"
          onClick={openPad}
          className="fixed bottom-24 right-4 md:bottom-8 md:right-8 z-[60] pointer-events-auto inline-flex items-center gap-2 rounded-full px-4 py-2.5 bg-[#0044FF] hover:bg-[#0044FF]/90 text-white shadow-[0_12px_40px_-12px_rgba(0,68,255,0.65)] border border-white/10 transition"
          title="Open OAK-Pad"
          data-testid="oakpad-fab"
        >
          <NotebookPen size={16} />
          <span className="font-display font-semibold text-sm">OAK-Pad</span>
          {snippets.length > 0 && (
            <span className="rounded-full bg-white/15 px-2 py-0.5 text-[11px] font-mono-oak" data-testid="oakpad-fab-badge">
              {snippets.length}
            </span>
          )}
        </button>
      </>
    );
  }

  const buildPdf = () => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 48;
    let y = margin;

    // Branded header band — solid OAK blue
    doc.setFillColor(0, 68, 255);
    doc.rect(0, 0, pageW, 64, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Oak AI Campus", margin, 30);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    doc.text("OAK-Pad · Master anything.", margin, 48);

    y = 64 + 32;
    doc.setTextColor(20, 20, 20);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.text("Your clipped notes", margin, y);
    y += 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(110, 110, 110);
    doc.text(`Exported ${new Date().toLocaleDateString()} · ${snippets.length} clip${snippets.length === 1 ? "" : "s"}`, margin, y);
    y += 28;

    snippets.forEach((s, i) => {
      // Page break guard — leave 80pt for next snippet meta + first lines
      if (y > pageH - 100) {
        doc.addPage();
        y = margin;
      }
      doc.setDrawColor(220);
      doc.line(margin, y, pageW - margin, y);
      y += 16;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      doc.setTextColor(0, 68, 255);
      const meta = s.source_title ? `#${i + 1} · ${s.source_title}` : `#${i + 1}`;
      doc.text(meta, margin, y);
      y += 14;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      const lines = doc.splitTextToSize(s.text, pageW - margin * 2);
      lines.forEach((ln) => {
        if (y > pageH - margin) {
          doc.addPage();
          y = margin;
        }
        doc.text(ln, margin, y);
        y += 14;
      });
      y += 12;
    });

    // Footer on every page
    const pageCount = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
      doc.setPage(p);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(`Oak AI Campus · oakaiapp.com · Page ${p} / ${pageCount}`, margin, pageH - 18);
    }
    return doc;
  };

  const exportPdf = async () => {
    if (snippets.length === 0) {
      toast.error("OAK-Pad is empty — clip something first.");
      return;
    }
    setExporting(true);
    try {
      const doc = buildPdf();
      doc.save(`OAK-Pad_${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  const sharePdf = async () => {
    if (snippets.length === 0) {
      toast.error("OAK-Pad is empty — clip something first.");
      return;
    }
    if (typeof navigator === "undefined" || !navigator.share) {
      toast.error("Native share isn't supported here. Tap Export PDF instead.");
      return;
    }
    setExporting(true);
    try {
      const doc = buildPdf();
      const blob = doc.output("blob");
      const file = new File([blob], `OAK-Pad_${new Date().toISOString().slice(0, 10)}.pdf`, {
        type: "application/pdf",
      });
      // canShare is the safer check on iOS Safari
      if (navigator.canShare && !navigator.canShare({ files: [file] })) {
        // Fallback: text-only share with a download hint
        await navigator.share({
          title: pageTitle,
          text: `My OAK-Pad notes — ${snippets.length} clip${snippets.length === 1 ? "" : "s"}.`,
        });
      } else {
        await navigator.share({
          title: pageTitle,
          text: `My OAK-Pad notes — ${snippets.length} clip${snippets.length === 1 ? "" : "s"}.`,
          files: [file],
        });
      }
      toast.success("Shared");
    } catch (e) {
      // User-cancelled share is not an error
      if (e?.name !== "AbortError") {
        toast.error(e?.message || "Share failed");
      }
    } finally {
      setExporting(false);
    }
  };

  const copyAll = async () => {
    if (snippets.length === 0) return;
    const text = snippets
      .map((s, i) => `#${i + 1}${s.source_title ? ` · ${s.source_title}` : ""}\n${s.text}`)
      .join("\n\n---\n\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("All clips copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[70] pointer-events-auto max-h-[85vh] flex flex-col rounded-t-3xl border-t border-white/10 shadow-[0_-30px_80px_-20px_rgba(0,0,0,0.7)]"
      style={{ backgroundColor: "#0A192F" }}
      data-testid="oakpad-drawer"
    >
      {/* Drag handle + header */}
      <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-white/5">
        <div className="flex items-center gap-2">
          <NotebookPen size={15} className="text-[#00FF9D]" />
          <span className="font-display font-semibold text-white text-sm">OAK-Pad</span>
          <span className="text-[11px] text-white/45 font-mono-oak" data-testid="oakpad-clip-count">
            {snippets.length} clip{snippets.length === 1 ? "" : "s"} · {totalChars} char{totalChars === 1 ? "" : "s"}
          </span>
          {synced && (
            <span
              className="hidden sm:inline-flex items-center gap-1 rounded-full bg-[#00FF9D]/10 border border-[#00FF9D]/25 px-1.5 py-0.5 text-[9px] text-[#00FF9D] font-mono-oak"
              title="Synced across your signed-in devices"
              data-testid="oakpad-sync-indicator"
            >
              ✓ synced
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="h-8 w-8 grid place-items-center rounded-full text-white/55 hover:text-white hover:bg-white/5 transition"
            title={expanded ? "Collapse" : "Expand"}
            data-testid="oakpad-toggle-collapse"
          >
            {expanded ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </button>
          <button
            type="button"
            onClick={closePad}
            className="h-8 w-8 grid place-items-center rounded-full text-white/55 hover:text-white hover:bg-white/5 transition"
            title="Close"
            data-testid="oakpad-close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {expanded && (
        <>
          {/* Snippets list */}
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2" data-testid="oakpad-list">
            {snippets.length === 0 ? (
              <div className="py-12 text-center text-white/55 text-sm">
                <div className="font-display text-base text-white">No clips yet</div>
                <p className="text-[13px] mt-1 max-w-xs mx-auto">
                  Long-press any course text (or right-click a selection) to clip it here.
                </p>
              </div>
            ) : (
              snippets.map((s, i) => (
                <div
                  key={s.id}
                  className="rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition p-3"
                  data-testid="oakpad-clip"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1 font-mono-oak">
                        #{i + 1}
                        {s.source_title ? <span className="text-white/55"> · {s.source_title}</span> : null}
                      </div>
                      <div className="text-[13px] text-white/85 whitespace-pre-wrap break-words leading-relaxed">
                        {s.text}
                      </div>
                    </div>
                    <button
                      onClick={() => removeSnippet(s.id)}
                      className="h-7 w-7 grid place-items-center rounded-full text-rose-300 hover:bg-rose-500/10 transition shrink-0"
                      title="Remove"
                      data-testid="oakpad-remove"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Actions */}
          <div className="px-5 py-3 border-t border-white/5 flex items-center gap-2 flex-wrap" data-testid="oakpad-actions">
            <button
              type="button"
              onClick={exportPdf}
              disabled={exporting || snippets.length === 0}
              className="btn-primary rounded-full px-4 py-2 text-xs font-display font-semibold inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="oakpad-export-pdf"
            >
              <FileDown size={13} /> Export PDF
            </button>
            <button
              type="button"
              onClick={sharePdf}
              disabled={exporting || snippets.length === 0}
              className="rounded-full px-4 py-2 text-xs font-display font-semibold border border-[#00FF9D]/40 bg-[#00FF9D]/[0.08] text-[#00FF9D] hover:bg-[#00FF9D]/[0.16] transition inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="oakpad-share"
            >
              <Share2 size={13} /> Share
            </button>
            <button
              type="button"
              onClick={publishStudyGuide}
              disabled={publishing || !canPublish}
              className="rounded-full px-4 py-2 text-xs font-display font-semibold border border-[#ffb067]/40 bg-[#ffb067]/[0.08] text-[#ffb067] hover:bg-[#ffb067]/[0.16] transition inline-flex items-center gap-2 disabled:opacity-50"
              title={canPublish ? "Mint a public study guide page" : "Clip at least 3 snippets to publish"}
              data-testid="oakpad-publish"
            >
              {publishing ? <Loader2 size={13} className="animate-spin" /> : <Globe2 size={13} />}
              {publishing ? "Publishing…" : "Publish as Study Guide"}
            </button>
            <button
              type="button"
              onClick={copyAll}
              disabled={snippets.length === 0}
              className="rounded-full px-4 py-2 text-xs font-display border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white transition inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="oakpad-copy-all"
            >
              <ClipboardCopy size={13} /> Copy all
            </button>
            <div className="flex-1" />
            <button
              type="button"
              onClick={() => {
                if (snippets.length === 0) return;
                if (window.confirm(`Clear all ${snippets.length} clip${snippets.length === 1 ? "" : "s"}?`)) {
                  clearAll();
                  toast.success("OAK-Pad cleared");
                }
              }}
              disabled={snippets.length === 0}
              className="rounded-full px-3 py-2 text-xs text-rose-300 hover:bg-rose-500/10 transition inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="oakpad-clear"
            >
              <Trash2 size={12} /> Clear
            </button>
          </div>
        </>
      )}
    </div>
  );
}
